import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Mail, MapPin, Phone } from "lucide-react";
import { COMPANY, CONTACT_EMAIL, WHATSAPP_LINK, PRODUCT_IDS } from "@/data/site";
import logo from "@/assets/ANSEN-logowbr.png";

const LINKS = [
  { key: "about", to: "/about" },
  { key: "services", to: "/services" },
  { key: "quality", to: "/quality" },
  { key: "insights", to: "/insights" },
  { key: "contact", to: "/contact" },
];

export const Footer = () => {
  const { t } = useTranslation();
  return (
    <footer className="bg-neutral-950 text-neutral-300 border-t border-neutral-800" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            <img src={logo} alt="ANSEN Indonesia" className="h-8 w-auto brightness-0 invert" />
            <p className="mt-4 text-sm text-neutral-400 leading-relaxed">{t("footer.about")}</p>
            <div className="mt-6 space-y-1.5 text-xs text-neutral-500">
              <p>{COMPANY.legalEntity}</p>
              <p>NIB: {COMPANY.nib}</p>
              <p>NPWP: {COMPANY.npwp}</p>
            </div>
          </div>
          <div>
            <h3 className="font-heading text-sm font-bold uppercase tracking-widest text-white">{t("footer.quickLinks")}</h3>
            <ul className="mt-4 space-y-2.5">
              {LINKS.map((l) => (
                <li key={l.key}>
                  <Link to={l.to} className="text-sm text-neutral-400 hover:text-primary transition-colors" data-testid={`footer-link-${l.key}`}>
                    {t(`nav.${l.key}`)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="font-heading text-sm font-bold uppercase tracking-widest text-white">{t("footer.productsTitle")}</h3>
            <ul className="mt-4 space-y-2.5">
              {PRODUCT_IDS.map((id) => (
                <li key={id}>
                  <Link to={`/products/${id}`} className="text-sm text-neutral-400 hover:text-primary transition-colors" data-testid={`footer-product-${id}`}>
                    {t(`products.${id}.name`)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="font-heading text-sm font-bold uppercase tracking-widest text-white">{t("footer.contactTitle")}</h3>
            <ul className="mt-4 space-y-3 text-sm text-neutral-400">
              <li className="flex items-start gap-2.5">
                <MapPin className="h-4 w-4 mt-0.5 text-primary shrink-0" />
                {t("contact.officeValue")}
              </li>
              <li className="flex items-center gap-2.5">
                <Mail className="h-4 w-4 text-primary shrink-0" />
                <a href={`mailto:${CONTACT_EMAIL}`} className="hover:text-primary transition-colors" data-testid="footer-email-link">{CONTACT_EMAIL}</a>
              </li>
              <li className="flex items-center gap-2.5">
                <Phone className="h-4 w-4 text-primary shrink-0" />
                <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors" data-testid="footer-whatsapp-link">+62 813-3113-1286</a>
              </li>
            </ul>
          </div>
        </div>
        <div className="mt-14 pt-6 border-t border-neutral-800 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-neutral-500">© {new Date().getFullYear()} {COMPANY.name} · {COMPANY.legalEntity}. {t("footer.rights")}</p>
          <p className="text-xs font-medium uppercase tracking-widest text-neutral-500">{t("footer.tagline")}</p>
        </div>
      </div>
    </footer>
  );
};
